import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, Stack, Typography, Chip, Button, Alert, LinearProgress, TextField, MenuItem, InputAdornment,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import {
  FAMILY_VENDOR_OFFERINGS_QUERY, VENDOR_OFFERING_FIELD_DEFS_QUERY, MY_VENDOR_SELECTIONS_QUERY,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { formatPriceRange } from './OfferingDetails';
import { packageLabelsFor, OfferingPackagesView } from './OfferingPackages';
import { BookVendorDialog, BookingStatusChip, fmtBookingDate } from './VendorBooking';

import Paged from './Paged'; // lists beyond 10 are paged
// A family service page (Venues, Catering, ...): the contracted vendors for these services,
// limited to what the family's signed contract covers, with their packages and booking.
// Admins/matchmakers see and book for the family they are managing. Without `categories`
// every service is shown; `showFilters` adds a search box and a Service filter.
export default function ContractedOfferingsPanel({ categories, title = 'Contracted vendors', showFilters = false, initialSearch = '' }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const navigate = useNavigate();
  const isFamilyUser = ['bride_family', 'groom_family'].includes(user?.role?.name);
  const canBook = isFamilyUser || Boolean(family);
  const [booking, setBooking] = useState(null);
  const [booked, setBooked] = useState(null);
  const [service, setService] = useState('');
  const [search, setSearch] = useState(initialSearch);

  const { data, loading } = useQuery(FAMILY_VENDOR_OFFERINGS_QUERY, { variables: {}, fetchPolicy: 'cache-and-network' });
  const { data: defsData } = useQuery(VENDOR_OFFERING_FIELD_DEFS_QUERY);
  const { data: selData, refetch } = useQuery(MY_VENDOR_SELECTIONS_QUERY, {
    variables: { includeClosed: false }, skip: !canBook, fetchPolicy: 'cache-and-network',
  });
  const fieldDefs = defsData?.vendorOfferingFields;
  const inScope = (data?.vendorOfferings || []).filter(o => !categories || categories.includes(o.category));
  // Services present, for the Service filter
  const services = [...new Map(inScope.map(o => [o.category, o.categoryName])).entries()]
    .map(([name, label]) => ({ name, label })).sort((a, b) => a.label.localeCompare(b.label));
  const q = search.trim().toLowerCase();
  // Search covers the vendor, offering, city and package/item names (e.g. a dish)
  const offerings = inScope.filter(o => (!service || o.category === service) && (!q || [
    o.businessName, o.title, o.city, o.serviceArea, o.categoryName,
    ...(o.packages || []).flatMap(p => [p.name, ...(p.items || []).map(i => i.name)]),
  ].some(v => (v || '').toLowerCase().includes(q))));
  const vendorCount = new Set(offerings.map(o => o.vendorEmail || o.businessName)).size;
  const openBookings = (selData?.myVendorSelections || []).filter(b => ['requested', 'confirmed'].includes(b.status));

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
          <StorefrontIcon color="primary" />
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
          <Button size="small" onClick={() => navigate('/dashboard/service-providers')}>All service providers</Button>
        </Stack>
        {showFilters && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems={{ sm: 'center' }}>
            <TextField size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search vendor, city, package or item"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Service" value={service} onChange={e => setService(e.target.value)} sx={{ minWidth: 220 }}>
              <MenuItem value="">All services</MenuItem>
              {services.map(s => <MenuItem key={s.name} value={s.name}>{s.label}</MenuItem>)}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {vendorCount} vendor{vendorCount === 1 ? '' : 's'} · {offerings.length} offering{offerings.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
        )}
        {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
        {booked && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBooked(null)}>
            {booked.count > 1 ? `${booked.count} booking requests` : 'Booking request'} sent to {booked.businessName} for {fmtBookingDate(booked.requiredDate)}. They'll be notified and confirm it.
          </Alert>
        )}
        {data && !offerings.length && (
          <Alert severity="info">
            {inScope.length ? 'No vendors match these filters.' : canBook
              ? 'No contracted vendors here yet. They appear once the contract covering this service is signed.'
              : 'No vendor has a signed contract for this service yet.'}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Paged items={offerings}>{rows => rows.map(o => (
            <Grid item xs={12} md={6} key={o.id}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, height: '100%' }}>
                <Chip size="small" color="primary" label={o.categoryName} sx={{ mb: 0.5 }} />
                <Typography fontWeight={700}>{o.title}</Typography>
                <Typography variant="body2" color="text.secondary">{o.businessName} · {o.city}</Typography>
                <Typography variant="body2" fontWeight={600} color="primary.main" mt={0.5}>{formatPriceRange(o)}</Typography>
                <OfferingPackagesView
                  labels={packageLabelsFor(fieldDefs, o.category)} packages={o.packages} dense
                  onBook={canBook ? (p) => setBooking({ offering: o, packageId: p.id }) : undefined}
                />
                {openBookings.filter(b => b.offeringId === o.id).map(b => (
                  <Stack key={b.id} direction="row" spacing={1} alignItems="center" mt={1}>
                    <Typography variant="body2" fontWeight={600}>Booked for {fmtBookingDate(b.requiredDate)}</Typography>
                    <BookingStatusChip status={b.status} />
                  </Stack>
                ))}
                {canBook && (
                  <Button variant="contained" size="small" sx={{ mt: 1.5 }} onClick={() => setBooking({ offering: o, packageId: '' })}>
                    Book {o.businessName}
                  </Button>
                )}
              </Box>
            </Grid>
          ))}</Paged>
        </Grid>
      </CardContent>
      <BookVendorDialog
        open={Boolean(booking)}
        offering={booking?.offering}
        initialPackageId={booking?.packageId}
        labels={booking ? packageLabelsFor(fieldDefs, booking.offering.category) : null}
        forFamily={isFamilyUser ? null : family?.displayName}
        onClose={() => setBooking(null)}
        onBooked={(b, all) => { setBooking(null); setBooked({ ...b, count: all?.length || 1 }); refetch(); }}
      />
    </Card>
  );
}
